import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { supplierRequestService, getErrorMessage } from '../../../services/apiService';
import { useToast } from '../../../components/ui/Toast';

const ApprovalDialog = ({ request, action, onClose, onComplete }) => {
  const toast = useToast();
  const [rejectionReason, setRejectionReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [approvalSuccess, setApprovalSuccess] = useState(false);
  const [inviteUrl, setInviteUrl] = useState(null);
  const [updatedRequestData, setUpdatedRequestData] = useState(null);

  const isApprove = action === 'approve';

  const handleSubmit = async () => {
    if (!isApprove && !rejectionReason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isApprove) {
        const response = await supplierRequestService.approveRequest(request.id);
        // Response includes invite information if approval was successful
        // Response structure from apiService: response.data = { success: true, data: { ...request, invite: { invite_url, ... } } }
        const responseData = response?.data || response;
        const updatedRequest = responseData?.data || responseData;
        const inviteData = updatedRequest?.invite || response?.data?.invite || response?.invite;
        
        // Create updated request object
        const requestUpdate = {
          ...request,
          ...updatedRequest,
          status: 'APPROVED',
          invite: inviteData
        };
        
        if (inviteData?.invite_url) {
          setInviteUrl(inviteData.invite_url);
          setApprovalSuccess(true);
          setLoading(false);
          // Store updated request for passing to parent when Done is clicked
          setUpdatedRequestData(requestUpdate);
          // Don't close immediately - show success message with link
          return;
        } else {
          // Fallback: if no invite URL, complete immediately with updated request
          onComplete(requestUpdate);
        }
      } else {
        const response = await supplierRequestService.rejectRequest(request.id, rejectionReason);
        const responseData = response?.data || response;
        const updatedRequest = responseData?.data || responseData;
        
        // Create updated request object with rejection data
        const requestUpdate = {
          ...request,
          ...updatedRequest,
          status: 'REJECTED',
          rejection_reason: rejectionReason
        };
        
        // Pass updated data to parent
        onComplete(requestUpdate);
      }
    } catch (err) {
      console.error(`${action} failed:`, err);
      const errorMsg = getErrorMessage(err);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl border border-slate-200 max-w-md w-full p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-slate-900">
            {isApprove ? 'Approve Request' : 'Reject Request'}
          </h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full p-2 transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Request Info */}
        <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
          <p className="font-semibold text-slate-900 mb-1">{request.required_vendor_name}</p>
          <p className="text-sm text-slate-600">Requested by: {request.requested_by || request.user_name}</p>
          <p className="text-sm text-slate-600">Email: {request.vendor_email}</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
            <Icon name="AlertCircle" size={18} className="text-red-600" />
            <span className="text-sm text-red-800">{error}</span>
          </div>
        )}

        {/* Rejection Reason */}
        {!isApprove && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Rejection Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={4}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="Please provide a detailed reason for rejection..."
            />
          </div>
        )}

        {/* Confirmation Message */}
        {isApprove && !approvalSuccess && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <Icon name="Info" size={16} className="inline mr-2" />
              After approval, a vendor registration link will be automatically created and sent to <strong>{request.vendor_email}</strong>. The registration link will also be available in the request details for future reference.
            </p>
          </div>
        )}

        {/* Success Message with Link */}
        {isApprove && approvalSuccess && inviteUrl && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="CheckCircle" size={20} className="text-green-600" />
              <span className="font-semibold text-green-900">Request Approved Successfully!</span>
            </div>
            <p className="text-sm text-green-800 mb-3">
              A registration link has been created and sent to <strong>{request.vendor_email}</strong>.
            </p>
            <div>
              <label className="block text-sm font-semibold text-green-900 mb-2">
                Registration Link
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={inviteUrl}
                  className="flex-1 px-3 py-2 bg-white border border-green-300 rounded-lg text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(inviteUrl);
                    toast.success('Registration link copied to clipboard!');
                  }}
                  iconName="Copy"
                  className="border-green-300 text-green-700 hover:bg-green-100"
                >
                  Copy
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
            className="flex-1"
          >
            Cancel
          </Button>
          {approvalSuccess ? (
            <Button
              onClick={() => onComplete(updatedRequestData)}
              variant="success"
              className="flex-1"
              iconName="CheckCircle"
              iconPosition="left"
            >
              Done
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={loading}
              variant={isApprove ? 'success' : 'destructive'}
              className="flex-1"
              iconName={loading ? null : (isApprove ? 'CheckCircle' : 'XCircle')}
              iconPosition="left"
            >
              {loading ? (
                <>
                  <Icon name="Loader" size={18} className="animate-spin" />
                  Processing...
                </>
              ) : (
                isApprove ? 'Approve' : 'Reject'
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApprovalDialog;
